import { RecommendationReason, Recommendation } from '@snapi/types';
import { useState } from 'react';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useReportContext } from '../report-context';
import { ReportRecommendationCard } from '../report-recommendation-card';
import { Button } from '@snapi/luxury';
import { useToggle } from 'react-use';
import { motion } from 'framer-motion';
import { Variants } from 'framer-motion';
import { Swiper as SwiperType } from 'swiper';
import clsx from 'clsx';

const CardVariants: Variants = {
  open: {
    height: 'auto',
    opacity: 1,
    pointerEvents: 'auto',
  },
  closed: {
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
  },
};

interface ReportRecommendationSummaryProps {
  recommendations: ReportRecommendation[];
}

export interface ReportRecommendation {
  reason: RecommendationReason;
  categories?: string[];
  condition?: string[];
  recommendation: Recommendation;
  score: number;
}

export const ReportRecommendationSummary = ({
  recommendations,
}: ReportRecommendationSummaryProps) => {
  const { baby } = useReportContext();
  const [selected, setSelected] = useState<ReportRecommendation | null>(null);
  const [showAll, toggleShowAll] = useToggle(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<ReportRecommendation | null>(null);
  const [isRecommendationClicked, setIsRecommendationClicked] = useState(false);

  const handleSlideChange = (swiper: SwiperType) => {
    const currentSlideIndex = swiper.activeIndex;
    setCurrentSlideIndex(currentSlideIndex);
  };

  const firstSixRecommendations = recommendations.slice(0, 6);
  const otherRecommendations = recommendations.slice(6);

  function handleRecommendationClick(recommendation: ReportRecommendation) {
    if (selectedRecommendation === recommendation) {
      setIsRecommendationClicked(false);
    } else {
      setSelectedRecommendation(recommendation);
      setIsRecommendationClicked(true);
    }
  }

  return (
    <section className="flex flex-col bg-blue-light rou rounded-t-[40px]">
      <div className="flex flex-col gap-5 lg:gap-8 lg:flex-row container mb-10">
        <h2 className="flex basis-1/2 text-dark">{`${baby.name}'s Nutrition Plan`}</h2>
        <p className="flex basis-1/2">
          {baby.name}’s taste buds are developing, and it's important to take it
          slow when introducing new foods! We suggest starting with 2-3 new
          foods per week.
        </p>
      </div>
      <div className="container hidden xl:block">
        <div className="self-center grid gap-7 lg:grid-cols-3">
          {firstSixRecommendations.map((recommendation) => (
            <div onClick={() => handleRecommendationClick(recommendation)}>
              <ReportRecommendationCard
                key={recommendation.recommendation.name}
                value={recommendation}
                onExpand={() => setSelected(recommendation)}
                currentSlideIndex={currentSlideIndex}
                isClicked={selectedRecommendation === recommendation}
              />
            </div>
          ))}
        </div>
        <motion.div
          layout
          animate={showAll ? 'open' : 'closed'}
          transition={{ duration: 0.6 }}
          variants={CardVariants}
          className={clsx(
            showAll && 'mt-7',
            'self-center grid gap-7 lg:grid-cols-3'
          )}
        >
          {otherRecommendations.map((recommendation) => (
            <div onClick={() => handleRecommendationClick(recommendation)}>
              <ReportRecommendationCard
                key={recommendation.recommendation.name}
                value={recommendation}
                onExpand={() => setSelected(recommendation)}
                currentSlideIndex={currentSlideIndex}
                isClicked={selectedRecommendation === recommendation}
              />
            </div>
          ))}
        </motion.div>
        <div className="mt-10 flex justify-center">
          {recommendations.length > 6 ? (
            <Button size="md" onClick={toggleShowAll}>
              {showAll ? 'Hide' : 'See'} all {`(${recommendations.length})`}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="block xl:hidden">
        <Swiper
          slidesPerView="auto"
          spaceBetween={28}
          className="px-3 sm:px-10"
          onSlideChange={handleSlideChange}
          pagination={{
            enabled: true,
            clickable: true,
            type: 'bullets',
            bulletElement: 'span',
            bulletActiveClass: 'bg-body opacity-100',
          }}
          modules={[Pagination]}
        >
          <>
            {recommendations.map((recommendation) => (
              <SwiperSlide
                key={recommendation.recommendation.id}
                className="mb-12 w-[290px] sm:w-[380px]"
              >
                <ReportRecommendationCard
                  key={recommendation.recommendation.name}
                  value={recommendation}
                  onExpand={() => setSelected(recommendation)}
                  currentSlideIndex={currentSlideIndex}
                />
              </SwiperSlide>
            ))}
          </>
        </Swiper>
      </div>
    </section>
  );
};
